import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";

const STATUS_BADGES = {
    approved: "bg-green-100 text-green-800",
    pending: "bg-yellow-100 text-yellow-800",
    rejected: "bg-red-100 text-red-800",
    cancelled: "bg-gray-200 text-gray-700",
};

export default function ReportWeekendPasses() {
    const [tab, setTab] = useState("roster");
    const [loading, setLoading] = useState(true);
    const [passes, setPasses] = useState([]);
    const [roster, setRoster] = useState({ in_building: [], not_in_building: [], in_count: 0, out_count: 0 });
    const [properties, setProperties] = useState([]);
    const [fundingTypes, setFundingTypes] = useState([]);
    const [filters, setFilters] = useState({ property_id: "", status: "", funding_type: "", from: "", to: "" });
    const [search, setSearch] = useState("");

    const fundingLabel = (st) => fundingTypes?.find((f) => String(f.student_type) === String(st))?.label ?? st ?? "—";

    const exportPasses = () => {
        const cols = ["Student", "Reference", "Unit", "Funding", "Balance", "Pass date", "Return date", "Status", "Source", "Added by", "Approved by", "Verification URL"];
        const esc = (v) => `"${(v ?? "").toString().replace(/"/g, '""')}"`;
        const rows = filteredPasses.map((p) => [
            p.student, p.booking_reference, p.unit, fundingLabel(p.funding_type),
            p.balance_at_check != null ? p.balance_at_check : "",
            p.pass_date, p.return_date, p.status, p.source,
            p.created_by_name, p.approved_by_name, p.verification_url,
        ].map(esc).join(","));
        const csv = [cols.map(esc).join(","), ...rows].join("\r\n");
        const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `weekend-passes-${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const openChat = (userId, bookingId) => {
        if (!bookingId) return;
        window.open(`/booking/residents?user_id=${userId ?? ""}&booking_id=${bookingId}`, "_blank", "noopener,noreferrer");
    };

    const matches = (...fields) => {
        const q = search.trim().toLowerCase();
        if (!q) return true;
        return fields.some((f) => (f ?? "").toString().toLowerCase().includes(q));
    };

    const filteredPasses = passes.filter((p) => matches(p.student, p.booking_reference, p.unit));
    const filteredIn = roster.in_building.filter((r) => matches(r.student, r.unit, r.student_number));
    const filteredOut = roster.not_in_building.filter((r) => matches(r.student, r.unit));

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            Object.entries(filters).forEach(([k, v]) => {
                if (v !== "" && v !== null) params[k] = v;
            });
            const res = await axios.get(`${process.env.REACT_APP_API_URL}reports/weekend-passes`, { params });
            setPasses(res.data.passes || []);
            setRoster(res.data.roster || { in_building: [], not_in_building: [], in_count: 0, out_count: 0 });
            setProperties(res.data.properties || []);
            setFundingTypes(res.data.fundingTypes || []);
        } catch (e) {
            console.error("Failed to load weekend passes report", e);
        } finally {
            setLoading(false);
        }
    }, [filters]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const setField = (k, v) => setFilters((prev) => ({ ...prev, [k]: v }));

    return (
        <div className="w-auto bg-gray-100 my-6 xl:my-0 xl:mx-4 rounded-3xl flex-1 p-5">
            <div className="flex justify-between items-center mb-4">
                <h1 className="text-2xl font-bold">Pass-Outs</h1>
                <Link to="/reports/landing" className="text-sm text-blue-600 hover:text-blue-800">
                    ← Back to Reports
                </Link>
            </div>

            {/* Tabs */}
            <div className="flex gap-2 mb-4">
                <button
                    onClick={() => setTab("roster")}
                    className={`px-4 py-2 rounded-md text-sm font-medium ${tab === "roster" ? "bg-primary text-white" : "bg-white text-gray-700 border border-gray-300"}`}
                >
                    In / Out of Building
                </button>
                <button
                    onClick={() => setTab("passes")}
                    className={`px-4 py-2 rounded-md text-sm font-medium ${tab === "passes" ? "bg-primary text-white" : "bg-white text-gray-700 border border-gray-300"}`}
                >
                    All Pass-Outs
                </button>
            </div>

            {/* Filters */}
            <div className="bg-white shadow-sm rounded-lg p-4 mb-4 flex flex-wrap items-end gap-4">
                <div className="flex-1 min-w-[200px]">
                    <label className="block text-xs font-medium text-gray-500 mb-1">Search name</label>
                    <input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Type a student name…"
                        className="w-full rounded-md border-gray-300 shadow-sm text-sm"
                    />
                </div>
                <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Property</label>
                    <select
                        value={filters.property_id}
                        onChange={(e) => setField("property_id", e.target.value)}
                        className="rounded-md border-gray-300 shadow-sm text-sm"
                    >
                        <option value="">All properties</option>
                        {properties.map((p) => (
                            <option key={p.id} value={p.id}>{p.name}</option>
                        ))}
                    </select>
                </div>

                {tab === "passes" && (
                    <>
                        <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">Status</label>
                            <select value={filters.status} onChange={(e) => setField("status", e.target.value)} className="rounded-md border-gray-300 shadow-sm text-sm">
                                <option value="">All</option>
                                <option value="approved">Approved</option>
                                <option value="pending">Pending</option>
                                <option value="rejected">Rejected</option>
                                <option value="cancelled">Cancelled</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">Funding Type</label>
                            <select value={filters.funding_type} onChange={(e) => setField("funding_type", e.target.value)} className="rounded-md border-gray-300 shadow-sm text-sm">
                                <option value="">All</option>
                                {fundingTypes.map((f) => (
                                    <option key={f.student_type} value={f.student_type}>{f.label}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">From</label>
                            <input type="date" value={filters.from} onChange={(e) => setField("from", e.target.value)} className="rounded-md border-gray-300 shadow-sm text-sm" />
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">To</label>
                            <input type="date" value={filters.to} onChange={(e) => setField("to", e.target.value)} className="rounded-md border-gray-300 shadow-sm text-sm" />
                        </div>
                        <button
                            type="button"
                            onClick={exportPasses}
                            disabled={filteredPasses.length === 0}
                            className="rounded-md bg-green-600 text-white text-sm font-medium px-4 py-2 hover:bg-green-700 disabled:opacity-50"
                        >
                            Export to Excel
                        </button>
                    </>
                )}
            </div>

            {loading ? (
                <p className="text-gray-500">Loading…</p>
            ) : tab === "roster" ? (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Not in building */}
                    <div className="bg-white shadow-sm rounded-lg overflow-hidden">
                        <div className="px-4 py-3 bg-red-50 border-b border-red-100 flex justify-between items-center">
                            <h3 className="font-semibold text-red-800">Not in building (on pass-out)</h3>
                            <span className="text-sm font-bold text-red-800">{filteredOut.length}</span>
                        </div>
                        <table className="min-w-full divide-y divide-gray-100 text-sm">
                            <thead className="text-left text-xs text-gray-500 uppercase">
                                <tr><th className="px-4 py-2">Student</th><th className="px-4 py-2">Ref</th><th className="px-4 py-2">Unit</th><th className="px-4 py-2">Back by</th></tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                                {filteredOut.map((r) => (
                                    <tr key={r.booking_id} onClick={() => openChat(r.user_id, r.booking_id)} className="cursor-pointer hover:bg-gray-50" title="Open chat in new tab">
                                        <td className="px-4 py-2">{r.student}</td>
                                        <td className="px-4 py-2">{r.booking_reference || "—"}</td>
                                        <td className="px-4 py-2">{r.unit}</td>
                                        <td className="px-4 py-2">{r.return_date}</td>
                                    </tr>
                                ))}
                                {filteredOut.length === 0 && (
                                    <tr><td colSpan={4} className="px-4 py-4 text-gray-500 text-center">Everyone is in the building.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* In building */}
                    <div className="bg-white shadow-sm rounded-lg overflow-hidden">
                        <div className="px-4 py-3 bg-green-50 border-b border-green-100 flex justify-between items-center">
                            <h3 className="font-semibold text-green-800">In building</h3>
                            <span className="text-sm font-bold text-green-800">{filteredIn.length}</span>
                        </div>
                        <table className="min-w-full divide-y divide-gray-100 text-sm">
                            <thead className="text-left text-xs text-gray-500 uppercase">
                                <tr><th className="px-4 py-2">Student</th><th className="px-4 py-2">Ref</th><th className="px-4 py-2">Unit</th></tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                                {filteredIn.map((r) => (
                                    <tr key={r.booking_id} onClick={() => openChat(r.user_id, r.booking_id)} className="cursor-pointer hover:bg-gray-50" title="Open chat in new tab">
                                        <td className="px-4 py-2">{r.student}</td>
                                        <td className="px-4 py-2">{r.booking_reference || "—"}</td>
                                        <td className="px-4 py-2">{r.unit}</td>
                                    </tr>
                                ))}
                                {filteredIn.length === 0 && (
                                    <tr><td colSpan={3} className="px-4 py-4 text-gray-500 text-center">No residents found.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            ) : (
                <div className="bg-white shadow-sm rounded-lg overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-100 text-sm">
                        <thead className="text-left text-xs text-gray-500 uppercase">
                            <tr>
                                <th className="px-4 py-2">Student</th>
                                <th className="px-4 py-2">Ref</th>
                                <th className="px-4 py-2">Unit</th>
                                <th className="px-4 py-2">Funding</th>
                                <th className="px-4 py-2">Balance</th>
                                <th className="px-4 py-2">Pass</th>
                                <th className="px-4 py-2">Return</th>
                                <th className="px-4 py-2">Status</th>
                                <th className="px-4 py-2">Source</th>
                                <th className="px-4 py-2">By</th>
                                <th className="px-4 py-2">Link</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                            {filteredPasses.map((p) => (
                                <tr key={p.id} onClick={() => openChat(p.user_id, p.booking_id)} className="cursor-pointer hover:bg-gray-50" title="Open chat in new tab">
                                    <td className="px-4 py-2">{p.student}</td>
                                    <td className="px-4 py-2">{p.booking_reference || "—"}</td>
                                    <td className="px-4 py-2">{p.unit}</td>
                                    <td className="px-4 py-2">{fundingLabel(p.funding_type)}</td>
                                    <td className="px-4 py-2">{p.balance_at_check != null ? `R${p.balance_at_check}` : "—"}</td>
                                    <td className="px-4 py-2">{p.pass_date}</td>
                                    <td className="px-4 py-2">{p.return_date}</td>
                                    <td className="px-4 py-2">
                                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${STATUS_BADGES[p.status] ?? "bg-gray-100 text-gray-800"}`}>
                                            {p.status}
                                        </span>
                                    </td>
                                    <td className="px-4 py-2">{p.source}</td>
                                    <td className="px-4 py-2 text-xs text-gray-600">
                                        {p.created_by_name && <div>Added: {p.created_by_name}</div>}
                                        {p.approved_by_name && <div>Appr: {p.approved_by_name}</div>}
                                        {!p.created_by_name && !p.approved_by_name && "—"}
                                    </td>
                                    <td className="px-4 py-2">
                                        {p.verification_url && (
                                            <a href={p.verification_url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="text-blue-600 hover:underline">Open</a>
                                        )}
                                    </td>
                                </tr>
                            ))}
                            {filteredPasses.length === 0 && (
                                <tr><td colSpan={11} className="px-4 py-4 text-gray-500 text-center">No pass-outs found.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
